// src/client/scenes/safeguard/utils/index.ts
//
// Drop-in replacement. Public API unchanged (usePermissions, useRoles).
// No <Safeguard> call sites need edits: inline `useCases={[...]}` and
// `context={{ project }}` literals are neutralized INSIDE the hook by
// depending on the array's content and the context's fields (store
// entities with stable identities) instead of the wrappers' identities.
//
// Per-render cost before: 3 spreads of a ~150-key table + array clones,
// a possible recursive list walk (isInDiscussion → isInList), a fresh
// mock-role object, and a new result identity — on every render of every
// instance (×734 in the 22s trace). After: a join + a handful of
// reference comparisons unless roles/settings/context entities actually
// changed.

import React from "react";
import { useWorkspaceContext } from "@scenes/workspaceContext";
import {
  useAuthentication,
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneRole,
  useRetrievePlanzoneSettings,
  useRetrieveProjectsRoles,
} from "@planzone/view-models";
import { Identifiable, Project, role as roleMock } from "@planzone/dto";
import { UseCases } from "@planzone/core";
import { Context } from "../types";
import { permissions } from "../permissions";
import { updateFromSettings } from "./settings";
import { updateFromComments } from "./comments";
import { updateFromAllocation } from "@scenes/safeguard/utils/allocation";

// Module-level: the old code allocated `{ ...roleMock, uuid: "" }` on every
// render of every instance that had no matching project resource.
const EMPTY_ROLE = { ...roleMock, uuid: "" };
const NO_ROLES: string[] = [];

/**
 * Returns the previous object whenever the next one is shallow-equal, so
 * `children(permissions)` sees a stable identity when no boolean changed —
 * that stability is what lets React.memo'd children inside the render prop
 * bail out. Idempotent render-time ref caching; safe under StrictMode's
 * double render.
 */
function useShallowStable<T extends Record<string, unknown>>(next: T): T {
  const ref = React.useRef(next);
  const prev = ref.current;
  if (prev !== next) {
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    const same =
      prevKeys.length === nextKeys.length &&
      nextKeys.every((k) => prev[k] === next[k]);
    if (!same) ref.current = next;
  }
  return ref.current;
}

function useProjectRole(project?: Identifiable<Project>) {
  const { account, hasSession } = useAuthentication();
  const {
    projectResources,
    isRequestFailure,
    isRequestSuccess,
    isRequestPending,
    retrieveProjectsRoles,
  } = useRetrieveProjectsRoles();
  const projectResource =
    !!project &&
    projectResources.find((resource) => resource.project.uuid === project.uuid);

  React.useEffect(() => {
    hasSession &&
      !isRequestFailure &&
      !isRequestSuccess &&
      !isRequestPending &&
      retrieveProjectsRoles(account);
  }, [hasSession]);

  return projectResource ? projectResource.role : EMPTY_ROLE;
}

function useRoles({ project }: Context) {
  const { account, hasSession } = useAuthentication();
  const { planzone } = useRetrieveCurrentPlanzone();
  const ctx = useWorkspaceContext();
  const {
    role,
    isRequestFailure: isPlanzoneRequestFailure,
    isRequestSuccess: isPlanzoneRequestSuccess,
    isRequestPending: isPlanzoneRequestPending,
    retrievePlanzoneRole,
  } = useRetrievePlanzoneRole();
  const currentProjectRole = useProjectRole(
    ctx.type === "project" ? ctx.project : undefined,
  );
  const providedProjectRole = useProjectRole(project);

  React.useEffect(() => {
    hasSession &&
      !isPlanzoneRequestFailure &&
      !isPlanzoneRequestSuccess &&
      !isPlanzoneRequestPending &&
      retrievePlanzoneRole({ planzone, account });
  }, [planzone.uuid, hasSession]);

  // Stable tuple identity while the three roles are unchanged.
  return React.useMemo(
    () => [role, currentProjectRole, providedProjectRole],
    [role, currentProjectRole, providedProjectRole],
  );
}

function hasPermission(needles: string[], haystack: string[]) {
  return !!needles.find((role) => haystack.includes(role));
}

function capitalize(str: string) {
  return str[0].toUpperCase() + str.slice(1);
}

function usePermissions<UC extends UseCases[]>(
  useCases: UC,
  context: Context = {},
) {
  type Permissions = { [Key in `can${Capitalize<UC[number]>}`]: boolean };

  const { planzone } = useRetrieveCurrentPlanzone();
  const { account } = useAuthentication();
  const { settings } = useRetrievePlanzoneSettings(planzone);
  const [planzoneRole, currentProjectRole, providedProjectRole] =
    useRoles(context);

  // Neutralize inline literals at call sites:
  // - the array's CONTENT is the dependency, not its identity;
  // - the context's FIELDS are the dependencies — they are store entities
  //   whose identities only change when the entity actually changes.
  const useCasesKey = useCases.join("|");
  const { project, list, task, milestone, allocation, event, comment } =
    context;

  // The three table transforms read: settings flags; presence AND content
  // of these entities (isInList walks list.tasks/subLists, isOwnComment
  // reads comment.author, isOwnAllocation reads allocation.resource); and
  // account.resource. Entity identity is therefore the correct dep — store
  // updates are immutable, so a changed entity means a changed identity.
  const table = React.useMemo(() => {
    const ctx: Context = {
      project,
      list,
      task,
      milestone,
      allocation,
      event,
      comment,
    };
    return updateFromAllocation(
      account.resource,
      updateFromComments(
        account.resource,
        updateFromSettings(settings, permissions, ctx),
        ctx,
      ),
      ctx,
    );
  }, [
    settings,
    account.resource,
    project,
    list,
    task,
    milestone,
    allocation,
    event,
    comment,
  ]);

  const roleNames = React.useMemo(
    () => [
      planzoneRole.name,
      currentProjectRole.name,
      providedProjectRole.name,
    ],
    [planzoneRole, currentProjectRole, providedProjectRole],
  );

  const computed = React.useMemo(() => {
    const out = {} as Permissions;
    for (const useCase of useCases) {
      out[`can${capitalize(useCase)}` as keyof Permissions] = hasPermission(
        roleNames,
        table[useCase] ?? NO_ROLES,
      ) as Permissions[keyof Permissions];
    }
    return out;
    // `useCases` identity is deliberately excluded — useCasesKey covers it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useCasesKey, roleNames, table]);

  return useShallowStable(computed);
}

export { usePermissions, useRoles };
